import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Divider,
  Box,
  Tooltip,
  CircularProgress,
  makeStyles,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment
} from "@material-ui/core";
import {
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  History as HistoryIcon,
  EventNote as EventNoteIcon,
  PictureAsPdf as PdfIcon,
  DateRange as DateRangeIcon,
  CloudDownload as DownloadIcon,
  WhatsApp as WhatsAppIcon,
  Close as CloseIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { AuthContext } from "../../../context/Auth/AuthContext";

registerLocale("es", es);

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper, 
    borderRadius: 12,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    fontWeight: 600,
    "& svg": {
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
    },
  },
  inputContainer: {
    backgroundColor: theme.palette.type === 'dark' 
      ? theme.palette.action.hover 
      : theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
  },
  buttonContainer: {
    marginTop: theme.spacing(1),
    display: "flex",
    justifyContent: "flex-end",
  },
  listContainer: {
    padding: 0,
  },
  commentItem: {
    alignItems: "flex-start",
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`, 
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover, 
    },
  },
  avatar: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    fontSize: "1rem",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  commentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  userName: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
  },
  dateText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  commentBody: {
    color: theme.palette.text.primary,
    whiteSpace: "pre-wrap",
    lineHeight: 1.5,
  },
  editInput: {
    backgroundColor: theme.palette.action.selected,
    borderRadius: 4,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.disabled,
  },
  successButton: {
    color: theme.palette.success.main,
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
  errorButton: {
    color: theme.palette.error.main,
    marginRight: 8,
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  dialogSectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    fontWeight: 600,
    textTransform: "uppercase"
  },
  "@global": {
    ".react-datepicker-popper": {
      zIndex: 1000 + " !important",
    }
  }
}));

const MedicalObservations = ({
  observations = [],
  contactId,
  patientName,
  onCreated,
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [messageBody, setMessageBody] = useState(`Hola ${patientName || ""}, te envío tu historial de observaciones médicas.`);
  const [doctors, setDoctors] = useState([]);

  const getInitials = (name) => {
    if (!name) return "?";
    return name.replace(/\s+/, " ").split(" ").slice(0, 2).map((v) => v && v[0].toUpperCase()).join("");
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  };

  const filteredCount = useMemo(() => {
    if (!observations) return 0;

    return observations.filter((obs) => {
      const obsDate = new Date(obs.createdAt);
      
      if (startDate && obsDate < startDate) return false;
      
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (obsDate > endOfDay) return false;
      }

      const activeDoctorId = user.profile === "doctor" ? user.id : (selectedDoctorId ? Number(selectedDoctorId) : null);
      
      if (activeDoctorId && obs.userId !== activeDoctorId) return false;

      return true;
    }).length;
  }, [observations, startDate, endDate, selectedDoctorId, user]);
  
  const handleCreate = async () => {
    if (!comment.trim()) return;
    try {
      setLoading(true);
      await api.post(`/medical-history/patients/${contactId}/observations`, {
        comment,
        patientName: patientName || null,
      });
      setComment("");
      onCreated && onCreated();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (obs) => {
    setEditingId(obs.id);
    setEditingText(obs.comment);
  };

  const handleUpdate = async () => {
    if (!editingText.trim()) return;
    try {
      setLoading(true);
      await api.put(`/medical-history/observations/${editingId}`, {
        comment: editingText,
      });
      toast.success("Observación actualizada correctamente"); 
      setEditingId(null);
      setEditingText("");
      onCreated && onCreated();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };
  
  useEffect(() => {
    if (pdfModalOpen && user.profile !== "doctor" && doctors.length === 0) {
      const fetchDoctors = async () => {
        try {
          const { data } = await api.get("/users", { params: { profile: "doctor" } });
          setDoctors(data.users || []);
        } catch (err) {
          console.error(err);
        }
      };
      fetchDoctors();
    }
  }, [pdfModalOpen, user.profile, doctors.length]);

  const handleOpenPdfModal = () => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    
    setStartDate(start);
    setEndDate(end);
    setSelectedDoctorId("");
    setMessageBody(`Hola ${patientName || ""}, adjunto te envío el historial de observaciones médicas.`);
    setPdfModalOpen(true);
  };

  const handleClosePdfModal = () => {
    setPdfModalOpen(false);
  };

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const params = {
        patientName,
        startDate: startDate ? startDate.toISOString().split("T")[0] : undefined,
        endDate: endDate ? endDate.toISOString().split("T")[0] : undefined,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      if (user.profile !== "doctor" && selectedDoctorId) {
        params.doctorId = selectedDoctorId;
      }

      const { data } = await api.get("/medical-history/observations/pdf/" + contactId, {
        params,
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Historial_" + (patientName || "Paciente") + ".pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("PDF descargado correctamente");
      handleClosePdfModal();
    } catch (err) {
      toastError(err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSendPdf = async () => {
    setIsSending(true);
    try {
      const payload = {
        patientName,
        startDate: startDate ? startDate.toISOString().split("T")[0] : undefined,
        endDate: endDate ? endDate.toISOString().split("T")[0] : undefined,
        bodyMessage: messageBody,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      if (user.profile !== "doctor" && selectedDoctorId) {
        payload.doctorId = selectedDoctorId;
      }

      await api.post("/medical-history/observations/send-pdf/" + contactId, payload);
      
      toast.success("Historial enviado al paciente por chat");
      handleClosePdfModal();
    } catch (err) {
      toastError(err);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Paper variant="outlined" className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <EventNoteIcon fontSize="large" />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Observaciones Médicas
          </Typography>
        </div>
        
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PdfIcon />}
          onClick={handleOpenPdfModal}
          size="small"
        >
          Exportar / Enviar
        </Button>
      </div>

      <Box className={classes.inputContainer}>
        <TextField
          label="Nueva observación"
          multiline
          minRows={2}
          maxRows={6}
          fullWidth
          variant="outlined"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Escribe aquí los detalles..."
          InputLabelProps={{ shrink: true }}
        />
        <div className={classes.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreate}
            disabled={loading || !comment.trim()}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          >
            {loading ? "Guardando" : "Agregar"}
          </Button>
        </div>
      </Box>

      <Divider style={{ marginBottom: 24 }} />

      {observations.length === 0 ? (
        <div className={classes.emptyState}>
          <HistoryIcon style={{ fontSize: 48, marginBottom: 8, opacity: 0.5 }} />
          <Typography variant="body1" color="textSecondary">
            No hay observaciones registradas.
          </Typography>
        </div>
      ) : (
        <List className={classes.listContainer}>
          {observations.map((obs) => {
            const isEditing = editingId === obs.id;
            const userName = obs.user?.name || "Desconocido";

            return (
              <Fade in={true} key={obs.id}>
                <ListItem className={classes.commentItem} disableGutters style={{ padding: 16 }}>
                  <ListItemAvatar style={{ minWidth: 56 }}>
                    <Avatar className={classes.avatar} alt={userName}>
                      {getInitials(userName)}
                    </Avatar>
                  </ListItemAvatar>

                  <div style={{ width: "100%" }}>
                    <div className={classes.commentHeader}>
                      <Typography className={classes.userName}>
                        {userName}
                      </Typography>
                      <Typography className={classes.dateText}>
                        {formatDate(obs.createdAt)}
                      </Typography>
                    </div>

                    {isEditing ? (
                      <Box mt={1}>
                        <TextField
                          fullWidth
                          multiline
                          variant="outlined"
                          size="small"
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          className={classes.editInput}
                          autoFocus
                        />
                        <div className={classes.actions} style={{ justifyContent: "flex-end", marginTop: 8 }}>
                          <Tooltip title="Cancelar">
                            <IconButton
                              size="small"
                              onClick={handleCancelEdit}
                              className={classes.errorButton}
                              disabled={loading}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Guardar">
                            <IconButton
                              size="small"
                              onClick={handleUpdate}
                              className={classes.successButton}
                              disabled={loading}
                            >
                              {loading ? <CircularProgress size={20} /> : <CheckIcon />}
                            </IconButton>
                          </Tooltip>
                        </div>
                      </Box>
                    ) : (
                      <>
                        <Typography variant="body2" className={classes.commentBody}>
                          {obs.comment}
                        </Typography>

                        <Box display="flex" justifyContent="space-between" alignItems="flex-end" mt={1}>
                          <Box>
                            {obs.updatedBy && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary" 
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  fontSize: '0.75rem',
                                  opacity: 0.8,
                                  fontStyle: 'italic'
                                }}
                              >
                                <EditIcon style={{ fontSize: 12, marginRight: 4 }} />
                                Editado por&nbsp;<strong>{obs.updatedBy.name}</strong>&nbsp;el {formatDate(obs.updatedAt)}
                              </Typography>
                            )}
                          </Box>

                          <Tooltip title="Editar esta observación">
                            <IconButton
                              size="small"
                              onClick={() => handleStartEdit(obs)}
                              style={{ color: "inherit", opacity: 0.5 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </>
                    )}
                  </div>
                </ListItem>
              </Fade>
            );
          })}
        </List>
      )}

      <Dialog open={pdfModalOpen} onClose={handleClosePdfModal} maxWidth="sm" fullWidth>
        <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                Exportar Historial Médico
                <IconButton size="small" onClick={handleClosePdfModal}>
                    <CloseIcon />
                </IconButton>
            </Box>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
            
            <Typography variant="body2" color="textSecondary" gutterBottom>
                Selecciona el rango de fechas y las opciones para generar el reporte.
            </Typography>

            <Grid container spacing={2} style={{ marginTop: 8 }}>
                <Grid item xs={6}>
                     <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        locale="es"
                        dateFormat="dd/MM/yyyy"
                        customInput={
                            <TextField 
                                label="Desde" 
                                variant="outlined" 
                                size="small" 
                                fullWidth 
                                InputProps={{ startAdornment: <InputAdornment position="start"><DateRangeIcon /></InputAdornment> }}
                            />
                        }
                    />
                </Grid>
                <Grid item xs={6}>
                    <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        locale="es"
                        dateFormat="dd/MM/yyyy"
                        customInput={
                            <TextField 
                                label="Hasta" 
                                variant="outlined" 
                                size="small" 
                                fullWidth 
                                InputProps={{ startAdornment: <InputAdornment position="start"><DateRangeIcon /></InputAdornment> }}
                            />
                        }
                    />
                </Grid>

                {user.profile !== "doctor" && (
                    <Grid item xs={12}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Filtrar por Doctor (Opcional)</InputLabel>
                            <Select
                                value={selectedDoctorId}
                                onChange={(e) => setSelectedDoctorId(e.target.value)}
                                label="Filtrar por Doctor (Opcional)"
                                MenuProps={{
                                    getContentAnchorEl: null,
                                    anchorOrigin: {
                                        vertical: "bottom",
                                        horizontal: "left",
                                    },
                                    transformOrigin: {
                                        vertical: "top",
                                        horizontal: "left",
                                    }
                                }}
                            >
                                <MenuItem value=""><em>Todos los doctores (Recomendado)</em></MenuItem>
                                {doctors.map((d) => (
                                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Box 
                        p={1.5} 
                        borderRadius={8} 
                        style={{ 
                            backgroundColor: filteredCount > 0 ? "rgba(33, 150, 243, 0.1)" : "rgba(244, 67, 54, 0.1)",
                            border: filteredCount > 0 ? "1px solid #2196f3" : "1px solid #f44336",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <Typography 
                            variant="body2" 
                            style={{ 
                                fontWeight: 600, 
                                color: filteredCount > 0 ? "#1976d2" : "#d32f2f" 
                            }}
                        >
                            {filteredCount > 0 
                                ? "ℹ️ Se incluirán " + filteredCount + " observaciones en el reporte."
                                : "⚠️ No se encontraron observaciones con estos filtros."
                            }
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <Typography className={classes.dialogSectionTitle}>
                        Mensaje para envío (Opcional)
                    </Typography>
                    <TextField
                        multiline
                        rows={2}
                        variant="outlined"
                        fullWidth
                        value={messageBody}
                        onChange={(e) => setMessageBody(e.target.value)}
                        placeholder="Mensaje que acompañará al PDF en el chat..."
                    />
                </Grid>
            </Grid>

        </DialogContent>
        
        <DialogActions style={{ padding: 20 }}>
            <Button
                onClick={handleDownloadPdf}
                color="primary"
                variant="outlined"
                disabled={isDownloading || isSending || filteredCount === 0}
                startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            >
                {isDownloading ? "Descargando..." : "Descargar PDF"}
            </Button>

            <Box flexGrow={1} />
            
            <Button
                onClick={handleSendPdf}
                color="primary"
                variant="contained"
                disabled={isDownloading || isSending || filteredCount === 0}
                style={{ backgroundColor: (filteredCount === 0) ? undefined : "#25D366", color: "white" }} 
                startIcon={isSending ? <CircularProgress size={20} color="inherit" /> : <WhatsAppIcon />}
            >
                {isSending ? "Enviando..." : "Enviar por Chat"}
            </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MedicalObservations;